import { FC, ReactNode, useMemo, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import { AppPage, AppCard } from '../../components/general';
import CalendarRange, { DefaultRangeSelectType } from '../../components/common/CalendarRange';
import { VoxRoiResult, PricingState } from '../../models/voxRoi';
import { ProviderMark } from './ProviderMarks';
import styles from './VoxRoi.module.css';

export interface VoxRoiProps {
  status: 'locked' | 'unlocked';
  hasConfig: boolean;
  configLoadError: boolean;
  isConfigFetching: boolean;
  onRetryConfig: () => void;
  passphrase: string;
  onPassphraseChange: (value: string) => void;
  confirmPassphrase: string;
  onConfirmPassphraseChange: (value: string) => void;
  onUnlock: () => void;
  unlockError: string | null;
  isConfigLoading: boolean;
  dateRange: Date[] | undefined;
  onDateRangeChange: (range: Date[] | undefined) => void;
  result: VoxRoiResult | null;
  isUsageLoading: boolean;
  isCatalogLoading: boolean;
  isUsageFetching: boolean;
  usageError: boolean;
  catalogError: boolean;
  isCatalogFetching: boolean;
  onRetryUsage: () => void;
  onRetryCatalog: () => void;
  costRail?: ReactNode;
}

const METHODOLOGY_FOOTNOTE_ID = 'vox-roi-methodology';
const UNAVAILABLE = 'Unavailable';

const formatCurrencyDecimal = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const formatPercent = (value: number | null) =>
  value === null ? '—' : `${(value * 100).toFixed(1)}%`;

const breadcrumbs = [
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'VoX ROI Savings' },
];

const rangeOptions = [
  DefaultRangeSelectType.CurrentMonth,
  DefaultRangeSelectType.Past30Days,
  DefaultRangeSelectType.Past2Weeks,
  DefaultRangeSelectType.PastWeek,
];

interface PasswordFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  feedback?: boolean;
  onEnter?: () => void;
}

const PasswordField: FC<PasswordFieldProps> = ({ id, label, value, onChange, invalid, feedback, onEnter }) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className={styles.passwordField}>
      <label htmlFor={id} className={styles.lockFormLabel}>{label}</label>
      <div className={styles.passwordInputRow}>
        <InputText
          id={id}
          type={visible ? 'text' : 'password'}
          className={classNames({ 'p-invalid': invalid })}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={invalid}
          autoComplete="new-password"
          onKeyDown={(event) => { if (event.key === 'Enter') onEnter?.(); }}
        />
        <Button
          type="button"
          label="Show Password"
          text
          size="small"
          aria-pressed={visible}
          onClick={() => setVisible((previous) => !previous)}
        />
      </div>
      {feedback && value.length > 0 && (
        <small className={styles.passwordFeedback}>
          {value.length < 8 ? 'Use at least 8 characters.' : 'Strength: good'}
        </small>
      )}
    </div>
  );
};

export const VoxRoi: FC<VoxRoiProps> = ({
  status,
  hasConfig,
  configLoadError,
  isConfigFetching,
  onRetryConfig,
  passphrase,
  onPassphraseChange,
  confirmPassphrase,
  onConfirmPassphraseChange,
  onUnlock,
  unlockError,
  isConfigLoading,
  dateRange,
  onDateRangeChange,
  result,
  isUsageLoading,
  isCatalogLoading,
  isUsageFetching,
  usageError,
  catalogError,
  isCatalogFetching,
  onRetryUsage,
  onRetryCatalog,
  costRail,
}) => {
  if (status === 'locked') {
    if (configLoadError) {
      return (
        <AppPage title="VoX ROI Savings" breadcrumbs={breadcrumbs}>
          <AppCard title="Could not load configuration">
            <div className={styles.errorState}>
              <Message severity="error" text="Failed to load your encrypted ROI configuration. Please retry." />
              <Button label="Retry" icon="pi pi-refresh" size="small" onClick={onRetryConfig} loading={isConfigFetching} disabled={isConfigFetching} />
            </div>
          </AppCard>
        </AppPage>
      );
    }

    const hasError = !!unlockError;
    return (
      <AppPage title="VoX ROI Savings" breadcrumbs={breadcrumbs}>
        <AppCard title={hasConfig ? 'Unlock your ROI report' : 'Set up your ROI configuration'}>
          <div className={styles.lockFormLayout}>
            <p className={styles.lockFormHint}>
              Your cost configuration is encrypted. Enter your passphrase to {hasConfig ? 'unlock' : 'create'} it.
              Mezzo cannot read your costs and cannot recover a lost passphrase.
            </p>
            <PasswordField
              id="vox-roi-passphrase"
              label="Passphrase"
              value={passphrase}
              onChange={onPassphraseChange}
              invalid={hasError}
              feedback={!hasConfig}
              onEnter={hasConfig ? onUnlock : undefined}
            />
            {!hasConfig && (
              <>
                <PasswordField
                  id="vox-roi-passphrase-confirm"
                  label="Confirm passphrase"
                  value={confirmPassphrase}
                  onChange={onConfirmPassphraseChange}
                  invalid={hasError}
                  onEnter={onUnlock}
                />
                <Message
                  severity="warn"
                  text="Store your passphrase safely. Mezzo cannot recover it, and a lost passphrase makes your saved costs permanently unreadable."
                />
              </>
            )}
            {unlockError && <Message severity="error" text={unlockError} />}
            <div className={styles.unlockButton}>
              <Button label={hasConfig ? 'Unlock' : 'Continue setup'} icon="pi pi-lock-open" onClick={onUnlock} disabled={isConfigLoading} loading={isConfigLoading} />
            </div>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  const hasActivity = result !== null && result.totalUnitCount > 0;
  const hasPricingWarnings =
    result !== null &&
    (result.warnings.hasIncompletePricing || result.warnings.afterFullyUnpriced);
  const reportLoading = isUsageLoading || isCatalogLoading;

  return (
    <AppPage
      title="VoX ROI Savings"
      subtitle="Cost per Unit Comparison Before and After"
      breadcrumbs={breadcrumbs}
      headerEnd={
        <div className={styles.headerEndGroup}>
          {hasActivity && <MethodologyLink />}
          <CalendarRange
            rangeSelectOptions={rangeOptions}
            value={dateRange}
            onValueChange={onDateRangeChange}
            commitOnCompleteRange
          />
        </div>
      }
    >
      <div className={costRail ? styles.twoPane : undefined}>
        {costRail && <aside className={styles.railPane}>{costRail}</aside>}
        <div className={styles.reportPane}>
          {catalogError && (
            <AppCard>
              <div className={styles.errorState}>
                <Message severity="error" text="Failed to load the product catalog. Your ROI report can't be built without it." />
                <Button label="Retry" icon="pi pi-refresh" size="small" onClick={onRetryCatalog} loading={isCatalogFetching} disabled={isCatalogFetching} />
              </div>
            </AppCard>
          )}

          {!catalogError && usageError && (
            <AppCard>
              <div className={styles.errorState}>
                <Message
                  severity="error"
                  text="Couldn't load VoX usage for this date range. Please retry, or adjust the date range."
                />
                <Button label="Retry" icon="pi pi-refresh" size="small" onClick={onRetryUsage} loading={isUsageFetching} disabled={isUsageFetching} />
              </div>
            </AppCard>
          )}

          {!catalogError && !usageError && reportLoading && (
            <div className={styles.spinnerContainer}>
              <ProgressSpinner style={{ width: '50px', height: '50px' }} />
            </div>
          )}

          {!catalogError && !usageError && !reportLoading && result && hasPricingWarnings && (
            <WarningPanel result={result} />
          )}

          {!catalogError && !usageError && !reportLoading && result && result.totalUnitCount === 0 && !hasPricingWarnings && (
            <AppCard>
              <p className={styles.emptyStateText}>No VoX activity in this range</p>
            </AppCard>
          )}

          {!catalogError && !usageError && !reportLoading && hasActivity && (
            <>
              <ReportContent result={result!} />
              <MethodologyFootnote />
              <ZeroKnowledgeFooter />
            </>
          )}
        </div>
      </div>
    </AppPage>
  );
};

/* ===== Warnings (rendered before the hero and the totals they qualify) ===== */

const WarningPanel: FC<{ result: VoxRoiResult }> = ({ result }) => {
  const { unmappedUsage, unmappedOrderCount, productsMissingCost, hasIncompletePricing, afterFullyUnpriced } =
    result.warnings;
  // Ticket: show INCOMPLETE PRICING whenever any successful activity is unpriced or unmatched,
  // including the fully-unpriced case that renders After/savings as Unavailable.
  const showIncompleteBanner = hasIncompletePricing || afterFullyUnpriced;

  return (
    <div className={styles.warningPanel}>
      {showIncompleteBanner && (
        <div className={styles.incompletePricingBanner}>INCOMPLETE PRICING</div>
      )}

      {unmappedUsage.length > 0 && (
        <Message
          severity="warn"
          className={styles.warningMessage}
          content={
            <div className={styles.warningContent}>
              <i className="pi pi-exclamation-triangle" />
              <div>
                <div className={styles.warningTitle}>
                  {unmappedOrderCount.toLocaleString('en-US')} successful order{unmappedOrderCount === 1 ? '' : 's'} across{' '}
                  {unmappedUsage.length} unmatched activity group{unmappedUsage.length === 1 ? '' : 's'} aren&rsquo;t linked to a
                  priced product, so their cost is excluded from After totals.
                </div>
                <ul className={styles.warningList}>
                  {unmappedUsage.map((group) => (
                    <li key={group.signature}>
                      {group.vendor} &middot; {group.transactionName} &middot; {group.reportType}
                      {group.dataType ? <> &middot; {group.dataType}</> : null}
                      {group.isSmartOutreach ? <> &middot; Smart Outreach</> : null}
                      {' '}({group.successCount.toLocaleString('en-US')})
                    </li>
                  ))}
                </ul>
                <div className={styles.warningHint}>
                  Contact Mezzo Support to map this activity to a product in the catalog.
                </div>
              </div>
            </div>
          }
        />
      )}

      {productsMissingCost.length > 0 && (
        <Message
          severity="warn"
          className={styles.warningMessage}
          content={
            <div className={styles.warningContent}>
              <i className="pi pi-exclamation-triangle" />
              <div>
                <div className={styles.warningTitle}>
                  {productsMissingCost.length} product{productsMissingCost.length === 1 ? '' : 's'} had activity with no
                  configured cost, so those orders are unpriced.
                </div>
                <ul className={styles.warningList}>
                  {productsMissingCost.map((gap) => (
                    <li key={gap.productKey}>
                      {gap.productDisplayName} ({gap.vendor}) &middot; {gap.affectedOrderCount.toLocaleString('en-US')} order
                      {gap.affectedOrderCount === 1 ? '' : 's'} on {gap.dates.join(', ')}
                    </li>
                  ))}
                </ul>
                <div className={styles.warningHint}>
                  Add a cost for these dates in Provider &amp; Product Costs to complete the report.
                </div>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

/* ===== Report Content (unlocked state) ===== */

interface AfterVendorGroup {
  vendor: string;
  products: string[];
  knownSpend: number;
  pricingState: PricingState;
  minSortOrder: number;
}

const groupPricingState = (states: PricingState[]): PricingState => {
  const hasUnpriced = states.some((state) => state !== 'complete');
  if (!hasUnpriced) return 'complete';
  const hasPriced = states.some((state) => state !== 'missing');
  return hasPriced ? 'partial' : 'missing';
};

/** After-column display for a group/line total: never a bare $0.00 for missing pricing. */
const afterTotalDisplay = (knownSpend: number, pricingState: PricingState): string => {
  if (pricingState === 'missing') return 'Price missing';
  if (pricingState === 'partial') return `${formatCurrencyDecimal(knownSpend)} known, partial`;
  return formatCurrencyDecimal(knownSpend);
};

const MetricLabel: FC<{ label: string; qualifier?: 'KNOWN SUBTOTAL' | 'UPPER BOUND'; helper?: string }> = ({
  label,
  qualifier,
  helper,
}) => (
  <span className={styles.heroLabel}>
    {label}
    {qualifier && <span className={styles.metricQualifier}> · {qualifier}</span>}
    {helper && <span className={styles.metricHelper}>{helper}</span>}
  </span>
);

const MethodologyLink: FC = () => (
  <a href={`#${METHODOLOGY_FOOTNOTE_ID}`} className={styles.methodologyLink}>
    <i className="pi pi-info-circle" />
    ROI calculation methodology &middot; Learn more
  </a>
);

const MethodologyFootnote: FC = () => (
  <div id={METHODOLOGY_FOOTNOTE_ID} className={styles.methodologyFootnote} tabIndex={-1}>
    <i className="pi pi-info-circle" />
    <span>
      BEFORE spend is modeled from each prior provider&rsquo;s per-order cost and orders per loan, multiplied
      by distinct loans in the window. AFTER spend uses actual transaction counts priced by the cost in
      effect on each order&rsquo;s date. Savings = BEFORE &minus; AFTER. When some activity is unmatched or has
      no configured cost, its spend is excluded, so reported savings and After totals are upper bounds — actual
      After spend may be higher and savings may be lower, including a larger loss.
    </span>
  </div>
);

const ZeroKnowledgeFooter: FC = () => (
  <div className={styles.zkFooter}>
    <i className="pi pi-shield" />
    <span>
      Zero-knowledge &mdash; your cost configuration is encrypted client-side before it reaches Mezzo.
      Mezzo cannot read your costs and cannot recover a lost passphrase.
    </span>
  </div>
);

const ReportContent: FC<{ result: VoxRoiResult }> = ({ result }) => {
  const afterGroups: AfterVendorGroup[] = useMemo(() => {
    const map = new Map<string, { vendor: string; products: string[]; knownSpend: number; states: PricingState[]; minSortOrder: number }>();
    for (const line of result.after.lines) {
      const existing = map.get(line.vendor);
      if (existing) {
        existing.products.push(line.productDisplayName);
        existing.knownSpend += line.knownSpend;
        existing.states.push(line.pricingState);
        existing.minSortOrder = Math.min(existing.minSortOrder, line.sortOrder);
      } else {
        map.set(line.vendor, {
          vendor: line.vendor,
          products: [line.productDisplayName],
          knownSpend: line.knownSpend,
          states: [line.pricingState],
          minSortOrder: line.sortOrder,
        });
      }
    }
    return Array.from(map.values())
      .map((group) => ({
        vendor: group.vendor,
        products: group.products,
        knownSpend: group.knownSpend,
        pricingState: groupPricingState(group.states),
        minSortOrder: group.minSortOrder,
      }))
      .sort((a, b) => {
        if (a.minSortOrder !== b.minSortOrder) return a.minSortOrder - b.minSortOrder;
        return a.vendor.localeCompare(b.vendor);
      });
  }, [result.after.lines]);

  const { afterFullyUnpriced } = result.warnings;
  const incompletePricing = result.warnings.hasIncompletePricing && !afterFullyUnpriced;
  const isLoss = result.savings.total !== null && result.savings.total < 0;

  const savingsPerLoanDisplay =
    afterFullyUnpriced || result.savings.perLoan === null
      ? UNAVAILABLE
      : formatCurrencyDecimal(Math.abs(result.savings.perLoan));
  const savingsTotalDisplay =
    afterFullyUnpriced || result.savings.total === null
      ? UNAVAILABLE
      : formatCurrencyDecimal(Math.abs(result.savings.total));
  const savingsPctDisplay =
    afterFullyUnpriced || result.savings.pct === null
      ? UNAVAILABLE
      : formatPercent(Math.abs(result.savings.pct));
  const afterSpendDisplay = afterFullyUnpriced ? UNAVAILABLE : formatCurrencyDecimal(result.after.knownSpend);
  const afterCostPerLoanDisplay = afterFullyUnpriced ? UNAVAILABLE : formatCurrencyDecimal(result.after.avgCostPerLoan);

  return (
    <>
      <div className={styles.savingsHero}>
        <div className={styles.savingsHeroTitle}>Your Estimated Savings &middot; VoX ROI</div>
        <div className={styles.savingsHeroSubtitle}>
          {result.totalUnitCount.toLocaleString('en-US')} units
          {!afterFullyUnpriced && result.savings.pct !== null && (
            <> &middot; cost per loan {result.savings.pct >= 0 ? 'down' : 'up'} {formatPercent(Math.abs(result.savings.pct))}</>
          )}
        </div>

        <div className={styles.savingsHeroFigures}>
          <div className={styles.savingsHeroMetric}>
            <span className={isLoss ? styles.heroValueLoss : styles.heroValue}>{savingsPerLoanDisplay}</span>
            <MetricLabel
              label={isLoss ? 'Added Cost Per Loan' : 'Savings Per Loan'}
              qualifier={incompletePricing ? 'UPPER BOUND' : undefined}
              helper={incompletePricing ? 'Actual savings may be lower, including a larger loss.' : undefined}
            />
          </div>
          <div className={styles.savingsHeroMetric}>
            <span className={styles.heroValueSecondary}>{savingsTotalDisplay}</span>
            <MetricLabel
              label={isLoss ? 'Total Added Cost' : 'Total Saved'}
              qualifier={incompletePricing ? 'UPPER BOUND' : undefined}
              helper={incompletePricing ? 'Actual savings may be lower, including a larger loss.' : undefined}
            />
          </div>
          <div className={styles.savingsHeroMetric}>
            <span className={styles.heroValueSecondary}>{savingsPctDisplay}</span>
            <MetricLabel
              label={isLoss ? 'Higher Cost Per Loan' : 'Lower Cost Per Loan'}
              qualifier={incompletePricing ? 'UPPER BOUND' : undefined}
              helper={incompletePricing ? 'Actual savings may be lower, including a larger loss.' : undefined}
            />
          </div>

          <div className={styles.costCallout}>
            <MetricLabel
              label="Cost Per Loan"
              qualifier={incompletePricing ? 'UPPER BOUND' : undefined}
              helper={incompletePricing ? 'Actual After cost per loan may be higher.' : undefined}
            />
            <div className={styles.costCalloutValues}>
              <span className={styles.costCalloutAmount}>{formatCurrencyDecimal(result.before.costPerLoan)}</span>
              <span className={styles.costCalloutArrow}>&rarr;</span>
              <span className={styles.costCalloutAmount}>{afterCostPerLoanDisplay}</span>
            </div>
          </div>
        </div>

        <div className={styles.savingsPills}>
          <div className={styles.savingsPill}>
            <div className={styles.pillLabel}>Units</div>
            <div className={styles.pillValue}>{result.totalUnitCount.toLocaleString('en-US')}</div>
          </div>
          <div className={styles.savingsPill}>
            <div className={styles.pillLabel}>Total Spend Before</div>
            <div className={styles.pillValue}>{formatCurrencyDecimal(result.before.totalSpend)}</div>
          </div>
          <div className={styles.savingsPill}>
            <div className={styles.pillLabel}>
              Total Spend After
              {incompletePricing && <span className={styles.metricQualifier}> · KNOWN SUBTOTAL</span>}
            </div>
            <div className={styles.pillValue}>{afterSpendDisplay}</div>
            {incompletePricing && (
              <div className={styles.pillHelper}>Actual After spend may be higher.</div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.columns}>
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <div className={`${styles.sectionIcon} ${styles.sectionIconBefore}`}>
              <i className="pi pi-chart-bar" />
            </div>
            <span className={`${styles.sectionTitle} ${styles.sectionTitleBefore}`}>BEFORE</span>
            <span className={styles.sectionTitleSuffix}>(Pre-VoX / Pre-Mezzo)</span>
            <span className={styles.providerBadge}>{result.before.providerCount}</span>
          </div>
          <div className={styles.sectionSubtitle}>Providers used before VoX</div>

          {result.before.providers.length > 0 ? (
            <table className={styles.providerTable}>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Product</th>
                  <th>Orders / Loan</th>
                  <th>Cost per Order</th>
                </tr>
              </thead>
              <tbody>
                {result.before.providers.map((provider) => (
                  <tr key={provider.productKey}>
                    <td>
                      <div className={styles.providerCell}>
                        <ProviderMark vendor={provider.vendor} showName />
                      </div>
                    </td>
                    <td>{provider.productDisplayName}</td>
                    <td>{provider.ordersPerLoan}</td>
                    <td>{provider.costPerOrder !== undefined ? formatCurrencyDecimal(provider.costPerOrder) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.noProviderMessage}>
              <Message severity="info" text="No prior providers configured" />
            </div>
          )}

          <div className={styles.summaryBox}>
            <div className={styles.summaryRow}>
              <span className={styles.summaryTitle}>Total modeled spend (Before)</span>
              <span className={styles.summaryValue}>{formatCurrencyDecimal(result.before.totalSpend)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Modeled cost per loan (Before)</span>
              <span className={styles.summaryValue}>{formatCurrencyDecimal(result.before.costPerLoan)}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Providers</span>
              <span className={styles.summaryValue}>{result.before.providerCount}</span>
            </div>
          </div>
        </div>

        <div className={`${styles.sectionCard} ${styles.sectionCardAfter}`}>
          <div className={styles.sectionHeader}>
            <div className={`${styles.sectionIcon} ${styles.sectionIconAfter}`}>
              <i className="pi pi-check-circle" />
            </div>
            <span className={`${styles.sectionTitle} ${styles.sectionTitleAfter}`}>AFTER</span>
            <span className={styles.sectionTitleSuffix}>(With VoX / Mezzo)</span>
            <span className={`${styles.providerBadge} ${styles.providerBadgeAfter}`}>{afterGroups.length}</span>
          </div>
          <div className={styles.sectionSubtitle}>Providers used with VoX</div>

          {afterGroups.length > 0 ? (
            <table className={`${styles.providerTable} ${styles.providerTableAfter}`}>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Products</th>
                  <th>Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {afterGroups.map((group) => (
                  <tr key={group.vendor}>
                    <td>
                      <div className={styles.providerCell}>
                        <ProviderMark vendor={group.vendor} showName />
                      </div>
                    </td>
                    <td>{group.products.join(', ')}</td>
                    <td>{afterTotalDisplay(group.knownSpend, group.pricingState)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.noProviderMessage}>
              <Message severity="info" text="No usage data in selected period" />
            </div>
          )}

          <div className={`${styles.summaryBox} ${styles.summaryBoxAfter}`}>
            <div className={styles.summaryRow}>
              <span className={`${styles.summaryTitle} ${styles.summaryTitleAfter}`}>
                Total actual spend (After)
                {incompletePricing && <span className={styles.metricQualifier}> · KNOWN SUBTOTAL</span>}
              </span>
              <span className={styles.summaryValueGreen}>{afterSpendDisplay}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Actual cost per loan (After)</span>
              <span className={styles.summaryValueGreen}>{afterCostPerLoanDisplay}</span>
            </div>
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Providers</span>
              <span className={styles.summaryValueGreen}>{afterGroups.length}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VoxRoi;
